import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Truck, UserCircle, Plus, X, CheckCircle, Calendar, CreditCard, Bank, Wallet, ArrowsClockwise } from "@phosphor-icons/react";

// ==============================================================
// FLEET PANEL — Vehicles + Driver Settings
// ==============================================================
export function FleetPanel() {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    (async () => {
      const [v, d] = await Promise.all([
        api.get("/fleet/vehicles").catch(() => ({ data: [] })),
        api.get("/fleet/drivers").catch(() => ({ data: [] })),
      ]);
      setVehicles(v.data || []);
      setDrivers(d.data || []);
    })();
  }, [refresh]);
  const bump = () => setRefresh((r) => r + 1);

  return (
    <div className="space-y-8">
      <VehiclesSection vehicles={vehicles} drivers={drivers} onChange={bump} />
      <DriversSection drivers={drivers} onChange={bump} />
    </div>
  );
}

function VehiclesSection({ vehicles, drivers, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ plate: "", name: "", driver_id: "", capacity_bins: 200, notes: "" });
  const submit = async () => {
    if (!f.plate) return;
    await api.post("/fleet/vehicles", { ...f, driver_id: f.driver_id || null });
    setF({ plate: "", name: "", driver_id: "", capacity_bins: 200, notes: "" });
    setOpen(false);
    onChange();
  };
  const patchVeh = async (id, patch) => { await api.patch(`/fleet/vehicles/${id}`, patch); onChange(); };
  const delVeh = async (id) => { if (window.confirm("Fahrzeug löschen?")) { await api.delete(`/fleet/vehicles/${id}`); onChange(); } };

  return (
    <section className="bg-white border border-black/10 p-5" data-testid="vehicles-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2"><Truck size={18} weight="bold" /> Fahrzeuge</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-black text-white hover:bg-zinc-800 h-9 text-xs" data-testid="add-vehicle-btn"><Plus size={14} className="mr-1" />Fahrzeug</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Fahrzeug anlegen</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Kennzeichen</Label><Input className="h-10" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value })} data-testid="vehicle-plate" /></div>
              <div><Label className="text-xs">Bezeichnung</Label><Input className="h-10" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="vehicle-name" /></div>
              <div><Label className="text-xs">Fahrer zuordnen (optional)</Label>
                <Select value={f.driver_id || "none"} onValueChange={(v) => setF({ ...f, driver_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-10" data-testid="vehicle-driver"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Keiner —</SelectItem>
                    {drivers.map((d) => <SelectItem key={d.user_id} value={d.user_id}>{d.name || d.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Kapazität (Tonnen/Tour)</Label><Input type="number" className="h-10" value={f.capacity_bins} onChange={(e) => setF({ ...f, capacity_bins: +e.target.value })} data-testid="vehicle-capacity" /></div>
              <div><Label className="text-xs">Notizen</Label><Input className="h-10" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
              <Button onClick={submit} className="w-full bg-black text-white hover:bg-zinc-800 rounded-none" data-testid="save-vehicle-btn">Speichern</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {vehicles.length === 0 ? (
        <p className="text-sm text-zinc-500">Keine Fahrzeuge hinterlegt.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase tracking-wide">
              <tr><th className="text-left py-2">Kennzeichen</th><th className="text-left">Bezeichnung</th><th className="text-left">Fahrer</th><th className="text-left">Kapazität</th><th className="text-left">Status</th><th></th></tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.vehicle_id} className="border-t border-black/10" data-testid={`vehicle-row-${v.vehicle_id}`}>
                  <td className="py-2 font-mono">{v.plate}</td>
                  <td>{v.name || "—"}</td>
                  <td>
                    <Select value={v.driver_id || "none"} onValueChange={(val) => patchVeh(v.vehicle_id, { driver_id: val === "none" ? null : val })}>
                      <SelectTrigger className="h-8 text-xs w-[200px]" data-testid={`vehicle-assign-${v.vehicle_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Keiner —</SelectItem>
                        {drivers.map((d) => <SelectItem key={d.user_id} value={d.user_id}>{d.name || d.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>{v.capacity_bins}</td>
                  <td>{v.active ? <span className="text-emerald-600 text-xs">aktiv</span> : <span className="text-zinc-500 text-xs">inaktiv</span>}</td>
                  <td className="text-right">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => patchVeh(v.vehicle_id, { active: !v.active })}>{v.active ? "Deaktivieren" : "Aktivieren"}</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs ml-2 text-red-600" onClick={() => delVeh(v.vehicle_id)} data-testid={`del-vehicle-${v.vehicle_id}`}><X size={12} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function DriversSection({ drivers, onChange }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ weekly_hours: 40, active_weekdays: [0, 1, 2, 3, 4], daily_target_hours: 8, hourly_wage_cents: 1500 });

  const openEdit = (d) => {
    setEditing(d);
    const s = d.settings || {};
    setForm({
      weekly_hours: s.weekly_hours || 40,
      active_weekdays: s.active_weekdays || [0, 1, 2, 3, 4],
      daily_target_hours: s.daily_target_hours || 8,
      hourly_wage_cents: s.hourly_wage_cents || 1500,
    });
  };
  const save = async () => {
    await api.put(`/fleet/drivers/${editing.user_id}/settings`, form);
    setEditing(null);
    onChange();
  };
  const toggleDay = (d) => setForm({ ...form, active_weekdays: form.active_weekdays.includes(d) ? form.active_weekdays.filter(x => x !== d) : [...form.active_weekdays, d].sort() });

  return (
    <section className="bg-white border border-black/10 p-5" data-testid="drivers-section">
      <h3 className="font-heading text-lg font-semibold tracking-tight mb-4 flex items-center gap-2"><UserCircle size={18} weight="bold" /> Fahrerplanung</h3>
      {drivers.length === 0 ? <p className="text-sm text-zinc-500">Keine Fahrer angelegt.</p> : (
        <div className="grid md:grid-cols-2 gap-4">
          {drivers.map((d) => {
            const s = d.settings || {};
            const ts = d.today_shift;
            const tsColor = ts?.kind === "sick" ? "bg-red-100 text-red-700" : ts?.kind === "vacation" ? "bg-amber-100 text-amber-700" : ts?.kind === "off" ? "bg-zinc-200 text-zinc-600" : "bg-emerald-100 text-emerald-700";
            const tsLabel = ts?.kind === "sick" ? "Krank" : ts?.kind === "vacation" ? "Urlaub" : ts?.kind === "off" ? "Frei" : "Arbeit";
            return (
              <div key={d.user_id} className="border border-black/10 p-4" data-testid={`driver-card-${d.user_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">{d.name || d.email}</div>
                    <div className="text-xs text-zinc-500">{d.email}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${tsColor}`}>{tsLabel}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-zinc-500">Wochenstd.:</span> <strong>{s.weekly_hours || 40}h</strong></div>
                  <div><span className="text-zinc-500">Zieltag:</span> <strong>{s.daily_target_hours || 8}h</strong></div>
                  <div className="col-span-2"><span className="text-zinc-500">Aktive Tage:</span> <strong>{(s.active_weekdays || [0,1,2,3,4]).map(w => WEEKDAYS[w]).join(", ")}</strong></div>
                  <div className="col-span-2"><span className="text-zinc-500">Stundenlohn:</span> <strong>€{((s.hourly_wage_cents || 1500) / 100).toFixed(2)}</strong></div>
                  {d.vehicle && <div className="col-span-2 text-emerald-700"><Truck size={12} className="inline mr-1" /> {d.vehicle.plate}</div>}
                </div>
                <Button size="sm" variant="outline" className="w-full mt-3 h-8 text-xs" onClick={() => openEdit(d)} data-testid={`edit-driver-${d.user_id}`}>Einstellungen</Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Fahrer-Einstellungen: {editing?.name || editing?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Wochenstunden</Label><Input type="number" step="0.5" className="h-10" value={form.weekly_hours} onChange={(e) => setForm({ ...form, weekly_hours: +e.target.value })} data-testid="driver-weekly-hours" /></div>
              <div><Label className="text-xs">Tägl. Ziel-Std.</Label><Input type="number" step="0.5" className="h-10" value={form.daily_target_hours} onChange={(e) => setForm({ ...form, daily_target_hours: +e.target.value })} data-testid="driver-daily-hours" /></div>
            </div>
            <div>
              <Label className="text-xs">Aktive Wochentage</Label>
              <div className="flex gap-1 mt-1">
                {WEEKDAYS.map((w, i) => (
                  <button key={w} type="button" onClick={() => toggleDay(i)} data-testid={`weekday-${i}`}
                    className={`w-9 h-9 text-xs border ${form.active_weekdays.includes(i) ? "bg-black text-white border-black" : "bg-white border-black/20 text-zinc-500"}`}>{w}</button>
                ))}
              </div>
            </div>
            <div><Label className="text-xs">Stundenlohn (Cent)</Label><Input type="number" className="h-10" value={form.hourly_wage_cents} onChange={(e) => setForm({ ...form, hourly_wage_cents: +e.target.value })} data-testid="driver-wage" />
              <div className="text-[11px] text-zinc-500 mt-1">= €{(form.hourly_wage_cents / 100).toFixed(2)}/h · Zieltag = €{(form.hourly_wage_cents * form.daily_target_hours / 100).toFixed(2)}</div>
            </div>
            <Button onClick={save} className="w-full bg-black text-white hover:bg-zinc-800 rounded-none" data-testid="save-driver-settings">Speichern</Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}


// ==============================================================
// SHIFT PLANNER — Kalender-Ansicht (14 Tage)
// ==============================================================
export function ShiftPlannerPanel() {
  const [drivers, setDrivers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [holidays, setHolidays] = useState({}); // { "YYYY-MM-DD": {name, ...} }
  const [range] = useState(14);
  const start = useMemo(() => new Date(new Date().toISOString().slice(0, 10)), []);
  const days = useMemo(() => Array.from({ length: range }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  }), [start, range]);
  // Export-Range (Default: aktueller Monat)
  const today = new Date();
  const monthFirst = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const monthLast = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [exportRange, setExportRange] = useState({ from: monthFirst, to: monthLast });
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    const [d, s, h] = await Promise.all([
      api.get("/fleet/drivers").catch(() => ({ data: [] })),
      api.get("/fleet/shifts", { params: { from_date: days[0], to_date: days[days.length - 1] } }).catch(() => ({ data: [] })),
      api.get("/holidays", { params: { from_date: days[0], to_date: days[days.length - 1] } }).catch(() => ({ data: { holidays: [] } })),
    ]);
    setDrivers(d.data || []);
    setShifts(s.data || []);
    const map = {};
    (h.data?.holidays || []).forEach(x => { map[x.date] = x; });
    setHolidays(map);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const shiftFor = (uid, day) => shifts.find(s => s.driver_id === uid && s.date === day);
  const setKind = async (uid, day, kind) => {
    // Feiertags-Warnung bei "work"
    if (kind === "work" && holidays[day]) {
      const ok = window.confirm(`Achtung: ${new Date(day).toLocaleDateString("de-DE")} ist ${holidays[day].name} (Feiertag).\n\nMöchtest du diesen Tag wirklich als „Arbeit" buchen?\nDruck auf „Abbrechen" setzt den Tag stattdessen auf „Frei".`);
      if (!ok) {
        await api.post("/fleet/shifts", { driver_id: uid, date: day, kind: "off", notes: `Feiertag: ${holidays[day].name}` });
        await load();
        return;
      }
    }
    await api.post("/fleet/shifts", { driver_id: uid, date: day, kind });
    await load();
  };

  const kinds = [
    { v: "work", label: "Arbeit", cls: "bg-emerald-100 hover:bg-emerald-200 text-emerald-700" },
    { v: "sick", label: "Krank", cls: "bg-red-100 hover:bg-red-200 text-red-700" },
    { v: "vacation", label: "Urlaub", cls: "bg-amber-100 hover:bg-amber-200 text-amber-700" },
    { v: "off", label: "Frei", cls: "bg-zinc-200 hover:bg-zinc-300 text-zinc-600" },
  ];

  const exportCsv = async () => {
    if (!exportRange.from || !exportRange.to) return;
    setExporting(true);
    try {
      const r = await api.get(`/fleet/shifts/export.csv`, { params: { from_date: exportRange.from, to_date: exportRange.to }, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `schichten-${exportRange.from}_${exportRange.to}.csv`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExportOpen(false);
    } catch (e) { alert("Export fehlgeschlagen: " + (e.response?.data?.detail || e.message)); }
    finally { setExporting(false); }
  };

  const setQuickRange = (preset) => {
    const now = new Date();
    if (preset === "current_month") {
      setExportRange({
        from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
        to: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10),
      });
    } else if (preset === "last_month") {
      setExportRange({
        from: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10),
        to: new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10),
      });
    } else if (preset === "ytd") {
      setExportRange({
        from: new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10),
        to: now.toISOString().slice(0, 10),
      });
    }
  };

  return (
    <div className="bg-white border border-black/10 p-5" data-testid="shift-planner">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2"><Calendar size={18} weight="bold" /> Schichtplan · nächste 14 Tage</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setExportOpen(v => !v)}
            className="text-xs font-medium px-3 py-1.5 rounded-full bg-black text-white hover:bg-zinc-800 inline-flex items-center gap-1.5"
            data-testid="shift-export-btn"
          >
            📊 CSV-Export…
          </button>
          <div className="flex gap-2 text-[10px]">
            {kinds.map(k => <span key={k.v} className={`px-2 py-0.5 rounded ${k.cls}`}>{k.label}</span>)}
            <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-700 font-medium" data-testid="holiday-legend">🎉 Feiertag</span>
          </div>
        </div>
      </div>
      {exportOpen && (
        <div className="mb-5 p-4 border border-dashed border-black/20 rounded-lg bg-zinc-50" data-testid="shift-export-panel">
          <div className="text-xs font-semibold mb-2">Zeitraum für Lohn-Export festlegen</div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Von</label>
              <input type="date" value={exportRange.from} onChange={(e) => setExportRange({ ...exportRange, from: e.target.value })} className="h-9 px-3 rounded-md border border-black/10 text-sm" data-testid="shift-export-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Bis</label>
              <input type="date" value={exportRange.to} onChange={(e) => setExportRange({ ...exportRange, to: e.target.value })} className="h-9 px-3 rounded-md border border-black/10 text-sm" data-testid="shift-export-to" />
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => setQuickRange("current_month")} className="text-[10px] px-2 py-1.5 border border-black/10 rounded hover:bg-white" data-testid="shift-range-current">Aktueller Monat</button>
              <button onClick={() => setQuickRange("last_month")} className="text-[10px] px-2 py-1.5 border border-black/10 rounded hover:bg-white" data-testid="shift-range-last">Vormonat</button>
              <button onClick={() => setQuickRange("ytd")} className="text-[10px] px-2 py-1.5 border border-black/10 rounded hover:bg-white" data-testid="shift-range-ytd">Jahr bis heute</button>
            </div>
            <button
              onClick={exportCsv}
              disabled={exporting || !exportRange.from || !exportRange.to}
              className="h-9 px-4 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 ml-auto"
              data-testid="shift-export-download"
            >
              {exporting ? "Erstelle…" : "CSV herunterladen"}
            </button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-2">
            Enthält alle Fahrer-Tage im gewählten Zeitraum (Arbeit/Krank/Urlaub/Frei) plus eine Summenzeile pro Fahrer für die Lohnbuchhaltung.
          </p>
        </div>
      )}
      {drivers.length === 0 ? <p className="text-sm text-zinc-500">Keine Fahrer.</p> : (
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse">
            <thead>
              <tr>
                <th className="text-left px-2 py-1 sticky left-0 bg-white">Fahrer</th>
                {days.map(d => {
                  const dt = new Date(d);
                  const wd = WEEKDAYS[(dt.getDay() + 6) % 7];
                  const hol = holidays[d];
                  return <th key={d} className={`px-1 py-1 text-center font-normal ${hol ? "bg-rose-100" : ""}`} title={hol ? `Feiertag: ${hol.name}` : ""} data-testid={hol ? `holiday-${d}` : undefined}>
                    <div className={`text-[10px] ${hol ? "text-rose-700 font-semibold" : "text-zinc-500"}`}>{wd}</div>
                    <div className={hol ? "text-rose-700" : ""}>{dt.getDate()}.{dt.getMonth() + 1}.</div>
                    {hol && <div className="text-[8px] text-rose-600 leading-tight truncate max-w-[70px]" title={hol.name}>🎉</div>}
                  </th>;
                })}
              </tr>
            </thead>
            <tbody>
              {drivers.map(drv => (
                <tr key={drv.user_id} className="border-t border-black/10">
                  <td className="px-2 py-1 sticky left-0 bg-white whitespace-nowrap font-medium">{drv.name || drv.email.split("@")[0]}</td>
                  {days.map(day => {
                    const s = shiftFor(drv.user_id, day);
                    const hol = holidays[day];
                    // Auf Feiertag ohne Eintrag → Vorschlag: "off"
                    const kk = s?.kind || (hol ? "off" : "work");
                    const cls = kinds.find(k => k.v === kk)?.cls || "";
                    return (
                      <td key={day} className={`p-0.5 ${hol ? "bg-rose-50" : ""}`} title={hol ? `Feiertag: ${hol.name}` : ""}>
                        <select
                          value={kk}
                          onChange={(e) => setKind(drv.user_id, day, e.target.value)}
                          className={`w-full text-[10px] px-1 py-1 rounded ${cls} border-none cursor-pointer`}
                          data-testid={`shift-${drv.user_id}-${day}`}
                        >
                          {kinds.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ==============================================================
// TOUR PLANNER — 5AM Cron Trigger
// ==============================================================
export function TourAutoplanPanel({ onGenerated }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [strategy, setStrategy] = useState("optimized");
  const run = async () => {
    setRunning(true); setResult(null);
    try {
      const endpoint = strategy === "optimized" ? "/scheduler/tours/run-optimized" : "/scheduler/tours/run-now";
      const r = await api.post(endpoint, { date, strategy });
      setResult(r.data);
      onGenerated && onGenerated();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || "Fehler" });
    } finally {
      setRunning(false);
    }
  };
  return (
    <div className="bg-gradient-to-br from-indigo-50 to-white border border-black/10 p-5" data-testid="tour-autoplan">
      <h3 className="font-heading text-lg font-semibold tracking-tight mb-2 flex items-center gap-2"><ArrowsClockwise size={18} weight="bold" /> Automatische Tourenplanung</h3>
      <p className="text-xs text-zinc-500 mb-4">Der Cron läuft täglich um <strong>05:00 Uhr</strong> und verteilt alle geplanten Jobs des Tages auf verfügbare Fahrer (unter Beachtung von Krank/Urlaub, aktiven Wochentagen, Fahrzeug-Kapazität und Arbeitszeit).</p>
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs">Datum</Label>
          <Input type="date" className="h-10 rounded-none" value={date} onChange={(e) => setDate(e.target.value)} data-testid="autoplan-date" />
        </div>
        <div>
          <Label className="text-xs">Strategie</Label>
          <Select value={strategy} onValueChange={setStrategy}>
            <SelectTrigger className="h-10 w-56 rounded-none" data-testid="autoplan-strategy"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="optimized">Geo-Cluster + Kapazität (empfohlen)</SelectItem>
              <SelectItem value="round_robin">Round-Robin (einfach)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={running} className="h-10 bg-black text-white hover:bg-zinc-800 rounded-none" data-testid="autoplan-run-btn">
          {running ? "Läuft…" : "Jetzt planen"}
        </Button>
      </div>
      {result && (
        <div className="mt-4 text-xs bg-white border border-black/10 p-3 space-y-2" data-testid="autoplan-result">
          {result.error ? <span className="text-red-600">Fehler: {result.error}</span> : (
            <>
              <div><strong>{result.tours_created}</strong> Touren · {result.jobs || 0} Jobs · {result.drivers || 0} Fahrer · Strategie: <strong>{result.strategy || "round_robin"}</strong>
                {result.unassigned_jobs > 0 && <span className="ml-2 text-amber-600">⚠ {result.unassigned_jobs} nicht zugewiesen (Kapazität überschritten)</span>}
                {result.reason && <em className="text-zinc-500"> · {result.reason}</em>}
              </div>
              {result.per_driver && result.per_driver.length > 0 && (
                <div className="space-y-1">
                  {result.per_driver.map((s, i) => (
                    <div key={s.driver_id} className="flex items-center gap-2 font-mono text-[11px] text-zinc-600">
                      <span className="w-40 truncate">{s.driver_id.slice(0, 12)}…</span>
                      <span>Jobs {s.jobs}</span>
                      <span>· {s.bins}/{s.capacity_bins} Tonnen</span>
                      <span>· {s.duration_min}/{s.capacity_min} Min</span>
                      <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (s.duration_min / s.capacity_min) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ==============================================================
// PAYMENTS PANEL — Vorkasse & SEPA
// ==============================================================
export function PaymentsPanel({ customers, onChange }) {
  const [filter, setFilter] = useState("all");
  const [payments, setPayments] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [payDialog, setPayDialog] = useState(null); // customer for vorkasse confirmation
  const [sepaDialog, setSepaDialog] = useState(null);
  const [payForm, setPayForm] = useState({ amount_cents: 0, notes: "" });
  const [sepaForm, setSepaForm] = useState({ holder_name: "", iban: "", bic: "" });

  const load = async () => {
    const [p, m] = await Promise.all([
      api.get("/payments").catch(() => ({ data: [] })),
      api.get("/sepa/mandates").catch(() => ({ data: [] })),
    ]);
    setPayments(p.data || []);
    setMandates(m.data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = customers.filter(c => filter === "all" ? true : (c.payment_method || "invoice") === filter);

  const setMethod = async (cid, method) => {
    await api.patch(`/customers/${cid}/payment-method`, { method });
    onChange && onChange();
  };
  const confirmPay = async () => {
    if (!payDialog || !payForm.amount_cents) return;
    await api.post("/payments/vorkasse/confirm", { customer_id: payDialog.customer_id, amount_cents: +payForm.amount_cents, notes: payForm.notes });
    setPayDialog(null); setPayForm({ amount_cents: 0, notes: "" }); load(); onChange && onChange();
  };
  const createMandate = async () => {
    if (!sepaDialog) return;
    try {
      await api.post("/sepa/mandates", { ...sepaForm, customer_id: sepaDialog.customer_id });
      setSepaDialog(null); setSepaForm({ holder_name: "", iban: "", bic: "" }); load(); onChange && onChange();
    } catch (e) { alert(e.response?.data?.detail || "Fehler"); }
  };

  const badgeFor = (m) => {
    if (m === "sepa") return { text: "SEPA", cls: "bg-blue-100 text-blue-700" };
    if (m === "vorkasse") return { text: "Vorkasse", cls: "bg-amber-100 text-amber-700" };
    if (m === "card") return { text: "Kreditkarte", cls: "bg-purple-100 text-purple-700" };
    return { text: "Rechnung", cls: "bg-zinc-100 text-zinc-700" };
  };

  return (
    <div className="space-y-6" data-testid="payments-panel">
      <section className="bg-white border border-black/10 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2"><Wallet size={18} weight="bold" /> Zahlungsarten pro Kunde</h3>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9 w-48 text-xs" data-testid="payment-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="invoice">Nur Rechnung</SelectItem>
              <SelectItem value="vorkasse">Nur Vorkasse</SelectItem>
              <SelectItem value="sepa">Nur SEPA</SelectItem>
              <SelectItem value="card">Nur Kreditkarte</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase">
              <tr><th className="text-left py-2">Kunde</th><th className="text-left">Nr.</th><th className="text-left">Methode</th><th className="text-left">Guthaben</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const m = c.payment_method || "invoice";
                const b = badgeFor(m);
                return (
                  <tr key={c.customer_id} className="border-t border-black/10" data-testid={`pay-row-${c.customer_id}`}>
                    <td className="py-2">{c.company_name || `${c.first_name} ${c.last_name}`}</td>
                    <td className="text-xs text-zinc-500">{c.customer_number || "—"}</td>
                    <td>
                      <select value={m} onChange={(e) => setMethod(c.customer_id, e.target.value)} className={`text-xs px-2 py-1 rounded ${b.cls} border-none`} data-testid={`pay-method-${c.customer_id}`}>
                        <option value="invoice">Rechnung</option>
                        <option value="vorkasse">Vorkasse</option>
                        <option value="sepa">SEPA</option>
                        <option value="card">Kreditkarte</option>
                      </select>
                    </td>
                    <td className="text-xs">€{((c.prepaid_balance_cents || 0) / 100).toFixed(2)}</td>
                    <td className="text-right">
                      {m === "vorkasse" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPayDialog(c)} data-testid={`vorkasse-confirm-${c.customer_id}`}><CheckCircle size={12} className="mr-1" />Eingang</Button>}
                      {m === "sepa" && <Button size="sm" variant="outline" className="h-7 text-xs ml-1" onClick={() => setSepaDialog(c)} data-testid={`sepa-add-${c.customer_id}`}><Bank size={12} className="mr-1" />Mandat</Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Payment History */}
      <section className="bg-white border border-black/10 p-5">
        <h3 className="font-heading text-lg font-semibold tracking-tight mb-3 flex items-center gap-2"><CreditCard size={18} weight="bold" /> Letzte Zahlungseingänge</h3>
        {payments.length === 0 ? <p className="text-sm text-zinc-500">Noch keine Zahlungen erfasst.</p> : (
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase"><tr><th className="text-left py-2">Datum</th><th className="text-left">Kunde</th><th className="text-left">Methode</th><th className="text-right">Betrag</th><th className="text-left">Notiz</th></tr></thead>
            <tbody>
              {payments.slice(0, 20).map(p => (
                <tr key={p.payment_id} className="border-t border-black/10">
                  <td className="py-2 text-xs">{new Date(p.received_at).toLocaleString("de-DE")}</td>
                  <td className="text-xs">{customers.find(c => c.customer_id === p.customer_id)?.first_name || p.customer_id.slice(0, 8)}</td>
                  <td className="text-xs">{p.method}</td>
                  <td className="text-right text-xs">€{(p.amount_cents / 100).toFixed(2)}</td>
                  <td className="text-xs text-zinc-500">{p.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* SEPA Mandates */}
      <section className="bg-white border border-black/10 p-5">
        <h3 className="font-heading text-lg font-semibold tracking-tight mb-3 flex items-center gap-2"><Bank size={18} weight="bold" /> SEPA-Mandate</h3>
        {mandates.length === 0 ? <p className="text-sm text-zinc-500">Noch keine SEPA-Mandate.</p> : (
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase"><tr><th className="text-left py-2">Ref.</th><th className="text-left">Kontoinhaber</th><th className="text-left">IBAN</th><th className="text-left">Kunde</th><th className="text-left">Status</th><th></th></tr></thead>
            <tbody>
              {mandates.map(m => (
                <tr key={m.mandate_id} className="border-t border-black/10">
                  <td className="py-2 text-xs font-mono">{m.reference}</td>
                  <td className="text-xs">{m.holder_name}</td>
                  <td className="text-xs font-mono">{m.iban_masked || "—"}</td>
                  <td className="text-xs">{customers.find(c => c.customer_id === m.customer_id)?.first_name || m.customer_id.slice(0, 8)}</td>
                  <td className="text-xs">{m.active ? <span className="text-emerald-600">aktiv</span> : <span className="text-zinc-400">widerrufen</span>}</td>
                  <td className="text-right">
                    {m.active && <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={async () => { await api.delete(`/sepa/mandates/${m.mandate_id}`); load(); }}>Widerrufen</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Vorkasse Dialog */}
      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Zahlungseingang bestätigen</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-500">Kunde: <strong>{payDialog?.first_name} {payDialog?.last_name}</strong></div>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Betrag (EUR)</Label>
              <Input type="number" step="0.01" className="h-10" value={payForm.amount_cents ? payForm.amount_cents / 100 : ""} onChange={(e) => setPayForm({ ...payForm, amount_cents: Math.round((+e.target.value || 0) * 100) })} data-testid="vorkasse-amount" />
            </div>
            <div><Label className="text-xs">Notiz</Label><Input className="h-10" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} placeholder="z. B. Überweisung eingegangen 09.07." /></div>
            <Button onClick={confirmPay} className="w-full bg-black text-white hover:bg-zinc-800 rounded-none" data-testid="vorkasse-save">Bestätigen</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SEPA Mandate Dialog */}
      <Dialog open={!!sepaDialog} onOpenChange={(o) => !o && setSepaDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>SEPA-Mandat erfassen</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-500 mb-2">Kunde: <strong>{sepaDialog?.first_name} {sepaDialog?.last_name}</strong></div>
          <div className="space-y-3">
            <div><Label className="text-xs">Kontoinhaber</Label><Input className="h-10" value={sepaForm.holder_name} onChange={(e) => setSepaForm({ ...sepaForm, holder_name: e.target.value })} data-testid="sepa-holder" /></div>
            <div><Label className="text-xs">IBAN</Label><Input className="h-10 font-mono uppercase" value={sepaForm.iban} onChange={(e) => setSepaForm({ ...sepaForm, iban: e.target.value.toUpperCase() })} placeholder="DE89 3704 0044 0532 0130 00" data-testid="sepa-iban" /></div>
            <div><Label className="text-xs">BIC (optional)</Label><Input className="h-10 font-mono uppercase" value={sepaForm.bic} onChange={(e) => setSepaForm({ ...sepaForm, bic: e.target.value.toUpperCase() })} data-testid="sepa-bic" /></div>
            <p className="text-[11px] text-zinc-500">Nach Erfassung wird die Zahlungsart des Kunden automatisch auf <strong>SEPA</strong> gesetzt und eine Mandatsreferenz generiert.</p>
            <Button onClick={createMandate} className="w-full bg-black text-white hover:bg-zinc-800 rounded-none" data-testid="sepa-save">Mandat anlegen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
